/**
 * Strukturaufschlag fuer boersenbasierte Preisbildung
 *
 * Der reale Angebotspreis = EEX-Baseload-Future + Strukturaufschlag.
 * Der Aufschlag deckt ab: Profilkosten (SLP-Lastform vs. Baseload),
 * Ausgleichsenergie, Risikopraemie, Vertriebsmarge des Versorgers.
 *
 * Default-Staffel kalibriert gegen ESOS24-Live-Preise (Juni 2026):
 *   Cal-27 +2,83 / Cal-28 +3,55 / Cal-29 +3,74 / Cal-31 +3,76 ct/kWh
 * Der Aufschlag steigt mit der Laufzeit (laenger = mehr Risikopraemie).
 *
 * QUELLE DER WAHRHEIT IST DER SERVER (GET/PUT /api/settings/aufschlag).
 * Frueher lag die Konfiguration pro Browser im localStorage — dadurch hat
 * JEDER VERTRIEBLER ANDERE PREISE GERECHNET. Der localStorage wird hier
 * nicht mehr verwendet.
 *
 * Architektur-Hinweis: die Rechen-Engine (kalkulationEngine.js) ruft
 * getAufschlagConfig() SYNCHRON auf. Deshalb:
 *   1. ensureAufschlagConfig() / loadAufschlagConfig() holt die Konfiguration
 *      einmalig vom Server (Aufrufer: useKalkulator, Settings).
 *   2. Das Ergebnis landet in einem Modul-Level-Cache (setAufschlagConfig).
 *   3. getAufschlagConfig() liest weiterhin synchron aus diesem Cache.
 *   4. Solange nichts geladen ist, gilt die DEFAULT_STAFFEL.
 * Ob der Cache vom Server stammt, verraet getAufschlagStatus() — der
 * Kalkulator verweigert die Boersen-Berechnung, wenn der Server nicht
 * erreichbar war, statt still mit Default-Werten zu rechnen.
 */

import { apiFetch } from '@/lib/api';

const ENDPOINT = '/api/settings/aufschlag';

// Default-Aufschlag pro Laufzeit (Jahre), in ct/kWh.
// Kalibriert gegen ESOS24 mit gemischtem Baseload (Durchschnitt der Cal-Jahre):
// Aufschlag ist nahezu konstant ~2,9 ct/kWh, da das Mischen der Cal-Jahre die
// Laufzeitstruktur bereits abbildet. Validiert: ±0,2 ct/kWh ggü. ESOS24.
export const DEFAULT_STAFFEL = {
  1: 2.90,  // 1 Jahr Laufzeit
  2: 2.90,  // 2 Jahre
  3: 2.90,  // 3 Jahre
  4: 2.95,  // 4 Jahre
  5: 3.00,  // 5 Jahre
};

// Verbrauchs-/Profil-Faktoren (Zuschlag/Abschlag in ct/kWh)
export const DEFAULT_FAKTOREN = {
  // Kleinere SLP-Kunden zahlen mehr Profilkosten
  slp_klein: 0.50,    // < 50.000 kWh
  slp_mittel: 0.20,   // 50.000 - 100.000 kWh
  slp_gross: 0.00,    // > 100.000 kWh
  rlm: -0.30,         // RLM-Kunden (registrierende Leistungsmessung)
};

export const DEFAULT_CONFIG = Object.freeze({
  staffel: { ...DEFAULT_STAFFEL },
  faktoren: { ...DEFAULT_FAKTOREN },
});

// ============================================================
// Modul-Level-Cache (die einzige Quelle fuer den synchronen Zugriff)
// ============================================================
let configCache = null;      // normalisierte Konfiguration vom Server
let updatedAt = null;        // updated_at der Server-Zeile
let source = 'default';      // 'server' | 'default' (noch nichts gespeichert) | 'unavailable'
let loadError = null;        // ehrlicher Fehlerstring, wenn der Server nicht erreichbar war
let isLoading = false;
let loadPromise = null;      // laufende Anfrage (Dedup)

const toNumber = (value, fallback) => {
  const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Bringt eine (Server-)Antwort in die Form, die die Rechen-Engine erwartet.
 * Der API-Vertrag nennt die Profilfaktoren `profil`, die Engine `faktoren` —
 * beide Schreibweisen werden akzeptiert.
 */
export function normalizeAufschlagConfig(raw) {
  const staffelRaw = raw?.staffel || {};
  const faktorenRaw = raw?.faktoren || raw?.profil || {};

  const staffel = {};
  Object.keys(DEFAULT_STAFFEL).forEach(jahre => {
    staffel[jahre] = toNumber(staffelRaw[jahre], DEFAULT_STAFFEL[jahre]);
  });
  // Zusaetzliche, serverseitig gepflegte Laufzeiten uebernehmen
  Object.keys(staffelRaw).forEach(jahre => {
    if (staffel[jahre] === undefined) {
      const n = toNumber(staffelRaw[jahre], null);
      if (n !== null) staffel[jahre] = n;
    }
  });

  const faktoren = {};
  Object.keys(DEFAULT_FAKTOREN).forEach(key => {
    faktoren[key] = toNumber(faktorenRaw[key], DEFAULT_FAKTOREN[key]);
  });

  return { staffel, faktoren };
}

/**
 * Setzt den Modul-Cache (z.B. nach einem erfolgreichen Server-Load/Save).
 */
export function setAufschlagConfig(config, meta = {}) {
  configCache = normalizeAufschlagConfig(config);
  updatedAt = meta.updated_at || null;
  source = meta.source || 'server';
  loadError = null;
  return configCache;
}

/**
 * Laedt die aktuelle Aufschlag-Konfiguration SYNCHRON aus dem Modul-Cache.
 * Wird aus der Rechen-Engine heraus aufgerufen und darf daher nicht async sein.
 * Ohne geladene Server-Konfiguration gilt die Default-Staffel.
 */
export function getAufschlagConfig() {
  if (configCache) {
    return { staffel: { ...configCache.staffel }, faktoren: { ...configCache.faktoren } };
  }
  return { staffel: { ...DEFAULT_STAFFEL }, faktoren: { ...DEFAULT_FAKTOREN } };
}

/**
 * Ehrlicher Zustand des Caches — damit die Oberflaeche nicht so tut, als
 * waere ein Default-Wert die serverseitig gepflegte Einstellung.
 * @returns {{loaded:boolean, loading:boolean, error:string|null, source:string, updated_at:string|null, isServerValue:boolean}}
 */
export function getAufschlagStatus() {
  return {
    loaded: source !== 'unavailable' && !loadError,
    loading: isLoading,
    error: loadError,
    source,
    updated_at: updatedAt,
    isServerValue: source === 'server' && !!configCache,
  };
}

/**
 * Holt die Konfiguration vom Server und schreibt sie in den Modul-Cache.
 * @returns {Promise<{ok:boolean, config:object, status:object, error?:string}>}
 */
export function loadAufschlagConfig({ force = false } = {}) {
  if (loadPromise && !force) return loadPromise;

  isLoading = true;
  loadPromise = (async () => {
    try {
      const resp = await apiFetch(ENDPOINT);
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const body = await resp.json();
          if (body?.error) msg = body.error;
        } catch { /* Body ist kein JSON */ }
        throw new Error(msg);
      }
      const data = await resp.json();

      if (!data || !data.staffel) {
        // Serverseitig noch nichts gepflegt -> Default gilt, das ist kein Fehler.
        configCache = null;
        updatedAt = null;
        source = 'default';
        loadError = null;
      } else {
        setAufschlagConfig(data, { updated_at: data.updated_at, source: 'server' });
      }
      return { ok: true, config: getAufschlagConfig(), status: getAufschlagStatus() };
    } catch (err) {
      // KEIN stiller Fallback auf alte Browser-Werte: der Zustand wird
      // ehrlich als "nicht geladen" markiert.
      source = 'unavailable';
      loadError = 'Strukturaufschlag konnte nicht vom Server geladen werden: ' + err.message;
      return { ok: false, config: getAufschlagConfig(), status: getAufschlagStatus(), error: loadError };
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * Stellt sicher, dass die Konfiguration geladen ist (dedupliziert parallele
 * Aufrufe) und wartet auf eine laufende Anfrage. Nach einem Fehler wird
 * erneut versucht.
 */
export function ensureAufschlagConfig() {
  if (loadPromise) {
    return loadPromise.then(res => (res.ok ? res : loadAufschlagConfig({ force: true })));
  }
  return loadAufschlagConfig();
}

/**
 * Speichert die Aufschlag-Konfiguration serverseitig (admin/manager).
 * @returns {Promise<{ok:boolean, config?:object, error?:string}>}
 */
export async function saveAufschlagConfig(config) {
  const normalized = normalizeAufschlagConfig(config);
  try {
    const resp = await apiFetch(ENDPOINT, {
      method: 'PUT',
      // API-Vertrag: { staffel, profil }
      body: JSON.stringify({ staffel: normalized.staffel, profil: normalized.faktoren }),
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      if (resp.status === 403) msg = 'Keine Berechtigung (nur Admin/Manager duerfen den Aufschlag aendern)';
      else {
        try {
          const body = await resp.json();
          if (body?.error) msg = body.error;
        } catch { /* Body ist kein JSON */ }
      }
      return { ok: false, error: msg };
    }
    const data = await resp.json().catch(() => null);
    const saved = setAufschlagConfig(data && data.staffel ? data : normalized, {
      updated_at: data?.updated_at || new Date().toISOString(),
      source: 'server',
    });
    // Nachfolgende ensureAufschlagConfig()-Aufrufe sollen den frischen Wert sehen
    loadPromise = Promise.resolve({ ok: true, config: getAufschlagConfig(), status: getAufschlagStatus() });
    return { ok: true, config: saved };
  } catch (err) {
    return { ok: false, error: 'Server nicht erreichbar: ' + err.message };
  }
}

/**
 * Berechnet den Strukturaufschlag fuer ein konkretes Lieferjahr.
 *
 * @param {number} lieferjahrOffset - 1 = erstes Lieferjahr, 2 = zweites, ...
 * @param {number} verbrauch - Jahresverbrauch in kWh
 * @param {string} zaehlverfahren - 'SLP' oder 'RLM'
 * @param {object} config - optional, sonst aus dem Modul-Cache
 * @returns {number} Aufschlag in ct/kWh
 */
export function getStrukturaufschlag(lieferjahrOffset, verbrauch, zaehlverfahren = 'SLP', config = null) {
  const cfg = config || getAufschlagConfig();

  // Basis-Staffel (groesster verfuegbarer Offset, falls darueber hinaus)
  const offsets = Object.keys(cfg.staffel).map(Number).sort((a, b) => a - b);
  const maxOffset = offsets[offsets.length - 1];
  const offset = Math.min(Math.max(1, lieferjahrOffset), maxOffset);
  let aufschlag = cfg.staffel[offset] ?? cfg.staffel[maxOffset] ?? 3.0;

  // Profil-/Verbrauchsfaktor
  if (zaehlverfahren === 'RLM') {
    aufschlag += cfg.faktoren.rlm;
  } else {
    if (verbrauch < 50000) aufschlag += cfg.faktoren.slp_klein;
    else if (verbrauch < 100000) aufschlag += cfg.faktoren.slp_mittel;
    else aufschlag += cfg.faktoren.slp_gross;
  }

  return Math.round(aufschlag * 10000) / 10000;
}
